import type {
  AiChatAction,
  AiClarificationSuggestionsAction,
  AiChatInternalMeta,
  AiChatPayload,
  AiNeedMoreInfoData,
  AiRequiredField,
  AiSuggestedFiltersAction,
  AiSuggestedQuery,
} from "@/feature/ai-planner/types/ai-chat.types";
import type {
  ChatClarificationData,
  ChatClarificationField,
} from "@/feature/ai-planner/types/chat-ui.types";

const ENGLISH_LOCALE = "en-US";

export type HighlightedTextSegment = {
  text: string;
  highlighted: boolean;
};

export type RichTextSegment = HighlightedTextSegment & {
  href?: string;
};

export type ClarificationOption = {
  code: string;
  title: string;
  description?: string;
};

export type ParsedClarificationMessage = {
  body: string;
  options: ClarificationOption[];
};

function asTrimmedString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function dedupeByKey<T>(items: readonly T[], getKey: (item: T) => string) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function toSlug(value: string) {
  return value
    .toLocaleLowerCase(ENGLISH_LOCALE)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function isSuggestedFiltersAction(
  action: AiChatAction,
): action is AiSuggestedFiltersAction {
  return action.type === "SUGGESTED_FILTERS";
}

function isClarificationSuggestionsAction(
  action: AiChatAction,
): action is AiClarificationSuggestionsAction {
  return action.type === "CLARIFICATION_SUGGESTIONS";
}

function normalizeStructuredSuggestedQueries(
  items?: readonly AiSuggestedQuery[],
) {
  const normalized = (items ?? []).flatMap((item, index) => {
    const label = asTrimmedString(item?.label);
    const query = asTrimmedString(item?.query);
    const category = asTrimmedString(item?.category) || undefined;
    const fieldKey = asTrimmedString(item?.fieldKey) || undefined;
    const fallbackLabel = label || query;

    if (!query || !fallbackLabel) {
      return [];
    }

    const fallbackIdSource = [category, fallbackLabel].filter(Boolean).join("-");
    const fallbackId = toSlug(fallbackIdSource) || `suggested-query-${index + 1}`;
    const id = asTrimmedString(item?.id) || fallbackId;

    return [
      {
        id,
        label: fallbackLabel,
        query,
        category,
        fieldKey,
      },
    ];
  });

  return dedupeByKey(normalized, (item) =>
    item.id.toLocaleLowerCase(ENGLISH_LOCALE),
  );
}

function normalizeLegacySuggestedQueries(suggestions?: readonly string[]) {
  const normalized = (suggestions ?? []).flatMap((suggestion, index) => {
    const value = asTrimmedString(suggestion);
    if (!value) {
      return [];
    }

    const slug = toSlug(value) || `suggestion-${index + 1}`;
    return [
      {
        id: `legacy-${slug}`,
        label: value,
        query: value,
      },
    ];
  });

  return dedupeByKey(normalized, (item) =>
    item.query.toLocaleLowerCase(ENGLISH_LOCALE),
  );
}

export function normalizeHighlightPhrases(phrases?: readonly string[]) {
  return dedupeByKey(
    (phrases ?? [])
      .map((phrase) => asTrimmedString(phrase))
      .filter(Boolean)
      .sort((left, right) => {
        if (right.length !== left.length) {
          return right.length - left.length;
        }
        return left.localeCompare(right, ENGLISH_LOCALE, {
          sensitivity: "base",
        });
      }),
    (phrase) => phrase.toLocaleLowerCase(ENGLISH_LOCALE),
  );
}

export function normalizeRequiredFields(fields?: readonly AiRequiredField[]) {
  const normalized = (fields ?? []).flatMap((field) => {
    const key = asTrimmedString(field?.key);
    const label = asTrimmedString(field?.label) || key;

    if (!key || !label) {
      return [];
    }

    return [
      {
        key,
        label,
        source: asTrimmedString(field?.source) || undefined,
      },
    ];
  });

  return dedupeByKey(normalized, (field) =>
    field.key.toLocaleLowerCase(ENGLISH_LOCALE),
  );
}

function hasClarificationFieldSuggestions(
  fields?: readonly AiRequiredField[],
) {
  return (fields ?? []).some(
    (field) => normalizeStructuredSuggestedQueries(field?.suggestions).length > 0,
  );
}

export function normalizeSuggestedQueries({
  payload,
  actions,
  internalMeta,
  needMoreInfoData,
}: {
  payload?: AiChatPayload;
  actions?: readonly AiChatAction[];
  internalMeta?: AiChatInternalMeta;
  needMoreInfoData?: AiNeedMoreInfoData | null;
}) {
  const clarificationActions = (actions ?? []).filter(
    isClarificationSuggestionsAction,
  );

  if (payload?.type === "NEED_MORE_INFO") {
    if (hasClarificationFieldSuggestions(needMoreInfoData?.required_fields)) {
      return [];
    }

    const clarificationQueries = dedupeByKey(
      [
        ...clarificationActions.flatMap((action) =>
          normalizeStructuredSuggestedQueries(action.items),
        ),
        ...normalizeStructuredSuggestedQueries(needMoreInfoData?.suggested_queries),
      ],
      (item) => item.id.toLocaleLowerCase(ENGLISH_LOCALE),
    );

    if (clarificationQueries.length) {
      return clarificationQueries;
    }
  }

  const suggestedFilterActions = (actions ?? []).filter(isSuggestedFiltersAction);

  const structuredQueries = dedupeByKey(
    [
      ...suggestedFilterActions.flatMap((action) =>
        normalizeStructuredSuggestedQueries(action.items),
      ),
      ...normalizeStructuredSuggestedQueries(internalMeta?.suggested_queries),
    ],
    (item) => item.id.toLocaleLowerCase(ENGLISH_LOCALE),
  );

  if (structuredQueries.length) {
    return structuredQueries;
  }

  return dedupeByKey(
    [
      ...normalizeLegacySuggestedQueries(
        suggestedFilterActions.flatMap((action) => action.suggestions ?? []),
      ),
      ...normalizeLegacySuggestedQueries(internalMeta?.suggested_filters),
    ],
    (item) => item.id.toLocaleLowerCase(ENGLISH_LOCALE),
  );
}

export function getHighlightedTextSegments(
  text: string,
  phrases?: readonly string[],
): HighlightedTextSegment[] {
  if (!text) {
    return [];
  }

  const normalizedPhrases = normalizeHighlightPhrases(phrases);
  if (!normalizedPhrases.length) {
    return [{ text, highlighted: false }];
  }

  const lowercaseText = text.toLocaleLowerCase(ENGLISH_LOCALE);
  const lowercasePhrases = normalizedPhrases.map((phrase) =>
    phrase.toLocaleLowerCase(ENGLISH_LOCALE),
  );

  const segments: HighlightedTextSegment[] = [];
  let cursor = 0;
  let plainTextStart = 0;

  while (cursor < text.length) {
    const matchedPhrase = lowercasePhrases.find((phrase) =>
      lowercaseText.startsWith(phrase, cursor),
    );

    if (!matchedPhrase) {
      cursor += 1;
      continue;
    }

    if (plainTextStart < cursor) {
      segments.push({
        text: text.slice(plainTextStart, cursor),
        highlighted: false,
      });
    }

    const matchEnd = cursor + matchedPhrase.length;
    segments.push({
      text: text.slice(cursor, matchEnd),
      highlighted: true,
    });

    cursor = matchEnd;
    plainTextStart = matchEnd;
  }

  if (plainTextStart < text.length) {
    segments.push({
      text: text.slice(plainTextStart),
      highlighted: false,
    });
  }

  return segments;
}

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<]+/giu;
const TRAILING_URL_PUNCTUATION = /[),.!?;:\]}]+$/u;

function trimTrailingUrlPunctuation(value: string) {
  let trimmed = value;

  while (trimmed && TRAILING_URL_PUNCTUATION.test(trimmed)) {
    trimmed = trimmed.replace(TRAILING_URL_PUNCTUATION, "");
  }

  return trimmed;
}

function normalizeUrlHref(value: string) {
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (/^www\./i.test(value)) {
    return `https://${value}`;
  }

  return null;
}

function splitSegmentByUrl(segment: HighlightedTextSegment): RichTextSegment[] {
  if (!segment.text) {
    return [];
  }

  const matches = Array.from(segment.text.matchAll(URL_PATTERN));
  if (!matches.length) {
    return [segment];
  }

  const parts: RichTextSegment[] = [];
  let lastIndex = 0;

  matches.forEach((match) => {
    const matchedText = match[0];
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      parts.push({
        text: segment.text.slice(lastIndex, matchIndex),
        highlighted: segment.highlighted,
      });
    }

    const trimmedUrlText = trimTrailingUrlPunctuation(matchedText);
    const trailingText = matchedText.slice(trimmedUrlText.length);

    if (trimmedUrlText) {
      parts.push({
        text: trimmedUrlText,
        highlighted: segment.highlighted,
        href: normalizeUrlHref(trimmedUrlText) ?? undefined,
      });
    }

    if (trailingText) {
      parts.push({
        text: trailingText,
        highlighted: segment.highlighted,
      });
    }

    lastIndex = matchIndex + matchedText.length;
  });

  if (lastIndex < segment.text.length) {
    parts.push({
      text: segment.text.slice(lastIndex),
      highlighted: segment.highlighted,
    });
  }

  return parts;
}

export function getRichTextSegments(
  text: string,
  phrases?: readonly string[],
): RichTextSegment[] {
  return getHighlightedTextSegments(text, phrases).flatMap(splitSegmentByUrl);
}

function normalizeMissingFields(missing?: readonly unknown[]) {
  return dedupeByKey(
    (missing ?? [])
      .map((item) => asTrimmedString(item))
      .filter(Boolean),
    (item) => item.toLocaleLowerCase(ENGLISH_LOCALE),
  );
}

function createSuggestionSignature(suggestions: readonly AiSuggestedQuery[]) {
  return suggestions
    .map((suggestion) =>
      [
        suggestion.id,
        suggestion.label,
        suggestion.query,
        suggestion.category,
        suggestion.fieldKey,
      ]
        .map((value) => (value ?? "").toLocaleLowerCase(ENGLISH_LOCALE))
        .join("|"),
    )
    .join("||");
}

function normalizeClarificationFields(
  fields?: readonly AiRequiredField[],
): ChatClarificationField[] {
  const normalizedFields = (fields ?? []).flatMap((field) => {
    const key = asTrimmedString(field?.key);
    const label = asTrimmedString(field?.label) || key;

    if (!key || !label) {
      return [];
    }

    return [
      {
        key,
        fieldKeys: [key],
        label,
        source: asTrimmedString(field?.source) || undefined,
        suggestions: normalizeStructuredSuggestedQueries(field.suggestions),
      },
    ];
  });

  const roomDetailFields = normalizedFields.filter((field) =>
    ["rooms", "guests"].includes(
      field.key.toLocaleLowerCase(ENGLISH_LOCALE),
    ),
  );

  if (roomDetailFields.length > 1) {
    const uniqueSignatures = dedupeByKey(
      roomDetailFields.map((field) => createSuggestionSignature(field.suggestions)),
      (signature) => signature,
    );

    if (uniqueSignatures.length <= 1) {
      const mergedRoomField: ChatClarificationField = {
        key: "room_details",
        fieldKeys: roomDetailFields.flatMap((field) => field.fieldKeys),
        label: "Room details",
        source: "room details",
        suggestions: dedupeByKey(
          roomDetailFields.flatMap((field) => field.suggestions),
          (suggestion) => suggestion.id.toLocaleLowerCase(ENGLISH_LOCALE),
        ),
      };

      return [
        ...normalizedFields.filter(
          (field) =>
            !["rooms", "guests"].includes(
              field.key.toLocaleLowerCase(ENGLISH_LOCALE),
            ),
        ),
        mergedRoomField,
      ];
    }
  }

  return normalizedFields;
}

function normalizeClarificationData(
  needMoreInfoData: AiNeedMoreInfoData | null,
): ChatClarificationData | undefined {
  if (!needMoreInfoData) {
    return undefined;
  }

  const fields = normalizeClarificationFields(needMoreInfoData.required_fields);
  const missing = normalizeMissingFields(needMoreInfoData.missing);

  if (!fields.length && !missing.length) {
    return undefined;
  }

  return {
    mode: asTrimmedString(needMoreInfoData.clarification_mode) || undefined,
    missing,
    fields,
  };
}

function normalizeClarificationBody(text: string) {
  return text
    .replace(/^[A-Za-z_]+:\s*/u, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeClarificationOption(rawOption: string) {
  const value = rawOption.trim();
  if (!value) {
    return null;
  }

  const matchedOption =
    /^([A-Z0-9]{2,8})\s+[—-]\s+(.+?)(?:\s+\((.+)\))?$/.exec(value);

  if (!matchedOption) {
    return null;
  }

  const code = matchedOption[1]?.trim();
  const title = matchedOption[2]?.trim();
  const description = matchedOption[3]?.trim();

  if (!code || !title) {
    return null;
  }

  return {
    code,
    title,
    description,
  };
}

export function parseClarificationMessage(
  text: string,
): ParsedClarificationMessage | null {
  const normalizedText = asTrimmedString(text);
  if (!normalizedText) {
    return null;
  }

  const optionsMarker = "Options:";
  const optionsIndex = normalizedText.indexOf(optionsMarker);
  if (optionsIndex < 0) {
    return null;
  }

  const body = normalizeClarificationBody(
    normalizedText.slice(0, optionsIndex).trim(),
  );
  const optionsText = normalizedText
    .slice(optionsIndex + optionsMarker.length)
    .trim();
  const options = optionsText
    .split(/\s*,\s*(?=[A-Z0-9]{2,8}\s+[—-]\s+)/u)
    .map(normalizeClarificationOption)
    .filter(Boolean) as ClarificationOption[];

  if (!body || !options.length) {
    return null;
  }

  return {
    body,
    options,
  };
}

function normalizeNeedMoreInfoData(payload: AiChatPayload) {
  if (payload.type !== "NEED_MORE_INFO" || !payload.data) {
    return null;
  }

  return payload.data as AiNeedMoreInfoData;
}

export function normalizeAssistantMessageMetadata(payload: AiChatPayload) {
  const needMoreInfoData = normalizeNeedMoreInfoData(payload);

  return {
    clarification: normalizeClarificationData(needMoreInfoData),
    highlightPhrases: normalizeHighlightPhrases(
      needMoreInfoData?.highlight_phrases,
    ),
    requiredFields: normalizeRequiredFields(needMoreInfoData?.required_fields),
    suggestedQueries: normalizeSuggestedQueries({
      payload,
      actions: payload.actions,
      internalMeta: payload._meta,
      needMoreInfoData,
    }),
  };
}
